import {
  BIND_AT_LEVEL,
  effectiveBonus,
  getItem,
  type ItemInstance,
  MAX_UPGRADE_LEVEL,
  nextUpgradeStep,
  RARITY_COLORS,
  type StatBonus,
  upgradeCost,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import {
  firstShortfall,
  formatGold,
  goldNeed,
  type Need,
  needIcon,
  needList,
  rarityHex,
  section,
  workAction,
} from './bench.js';
import { describeOnHover } from './item-hover.js';
import { addLevelBadge, paintItemIcon } from './item-icon.js';
import { formatStat, STAT_LABELS } from './item-tooltip.js';
import { uiFormat, uiText } from './text.js';

/**
 * The right side of the smith's window (ADR 0013): the piece chosen, what the
 * next level gives, its odds, what it takes against what the bag and the
 * purse hold, and the button that says what it does and costs. The preview
 * uses the server's rules; the outcome is the server's.
 */

/** The card describes the piece; it judges no level nor class (item-hover.ts). */
const ANYONE = { level: Number.MAX_SAFE_INTEGER, classId: 'warrior' } as const;

const STATS: readonly (keyof StatBonus)[] = ['dps', 'defence', 'health'];

export interface SmithDetailInput {
  instance: ItemInstance;
  worn: boolean;
  icons: CssAtlas | null;
  purse: number;
  owned: (templateId: string) => number;
  inReach: boolean;
  pending: boolean;
  onUpgrade: (itemId: string) => void;
}

export function renderSmithDetail(into: HTMLElement, input: SmithDetailInput): void {
  const { instance } = input;
  const template = getItem(instance.templateId);
  if (template === undefined) return;
  const level = instance.upgradeLevel;
  const step = nextUpgradeStep(level);
  const cost = upgradeCost(template, level);
  const top = step === undefined || cost === undefined || level >= MAX_UPGRADE_LEVEL;

  // The piece itself: its icon, its name and level, where it is.
  const card = document.createElement('div');
  card.className = 'smith__card';
  const slot = document.createElement('div');
  slot.className = 'slot';
  const icon = document.createElement('div');
  icon.className = `item item--${template.rarity}`;
  paintItemIcon(icon, template, input.icons, template.id);
  addLevelBadge(icon, level);
  describeOnHover(icon, template, () => ({ wearer: ANYONE, details: {}, instance }));
  slot.append(icon);
  const text = document.createElement('div');
  text.className = 'bench__text';
  const name = document.createElement('span');
  name.className = 'bench__name smith__name';
  name.style.color = rarityHex(RARITY_COLORS[template.rarity]);
  name.textContent = level > 0 ? `${uiText(template.name)} +${String(level)}` : uiText(template.name);
  const meta = document.createElement('span');
  meta.className = 'bench__meta';
  const where = input.worn ? uiText('Portée') : uiText('Dans ton sac');
  meta.textContent = instance.bound ? `${where} · ${uiText('Liée')}` : where;
  text.append(name, meta);
  card.append(slot, text);
  if (!top) {
    const jump = document.createElement('span');
    jump.className = 'smith__step';
    jump.textContent = `+${String(level)} → +${String(step.level)}`;
    card.append(jump);
  }
  into.append(card);

  if (top) {
    into.append(stats(effectiveBonus(template.bonus, level), undefined));
    const done = document.createElement('p');
    done.className = 'smith__muted';
    done.textContent = uiText('Cette pièce est au sommet de son art.');
    into.append(done);
    return;
  }

  into.append(
    stats(effectiveBonus(template.bonus, level), effectiveBonus(template.bonus, step.level)),
  );
  into.append(odds(step.chance));

  const material = getItem(cost.templateId);
  const needs: Need[] = [
    {
      icon: needIcon(material, cost.templateId, input.icons),
      name: uiText(material?.name ?? cost.templateId),
      have: input.owned(cost.templateId),
      need: cost.quantity,
    },
    goldNeed(cost.gold, input.purse),
  ];
  into.append(section(uiText('Coût')), needList(needs));

  if (!instance.bound && step.level >= BIND_AT_LEVEL) {
    const bind = document.createElement('p');
    bind.className = 'smith__bind';
    bind.textContent = uiText('Réussie, elle sera liée à ton personnage.');
    into.append(bind);
  }

  const why = input.pending
    ? ''
    : (firstShortfall(needs) ?? (input.inReach ? undefined : uiText('Approche-toi du forgeron.')));
  const action = workAction(
    uiFormat('Améliorer en +{level} · {gold}', { level: step.level, gold: formatGold(cost.gold) }),
    why,
    (button) => {
      button.disabled = true;
      input.onUpgrade(instance.id);
    },
  );
  into.append(action);
}

/** The odds, big, coloured, and as a bar; and what a failure costs. */
function odds(chance: number): HTMLElement {
  const box = document.createElement('div');
  box.className = 'smith__odds';
  box.dataset.odds = chance >= 50 ? 'good' : chance >= 10 ? 'fair' : 'poor';
  const label = document.createElement('span');
  label.className = 'smith__odds-label';
  label.textContent = uiText('Chance de réussite');
  const value = document.createElement('span');
  value.className = 'smith__chance';
  value.dataset.odds = box.dataset.odds;
  value.textContent = uiFormat('{chance} %', { chance });
  const meter = document.createElement('div');
  meter.className = 'smith__meter';
  const fill = document.createElement('span');
  fill.style.width = `${String(Math.max(2, Math.min(100, Math.round(chance))))}%`;
  meter.append(fill);
  const note = document.createElement('span');
  note.className = 'smith__odds-note';
  note.textContent = uiText('Échec : les matériaux sont perdus, la pièce ne bouge pas.');
  box.append(label, value, meter, note);
  return box;
}

/** Stats now and, when there is a next level, after it: what grows is shown in green. */
function stats(now: StatBonus, after: StatBonus | undefined): HTMLElement {
  const table = document.createElement('div');
  table.className = 'smith__stats';
  for (const stat of STATS) {
    if (now[stat] === 0 && (after?.[stat] ?? 0) === 0) continue;
    const label = document.createElement('span');
    label.textContent = uiText(STAT_LABELS[stat]);
    const current = document.createElement('span');
    current.textContent = formatStat(stat, now[stat]);
    table.append(label, current);
    if (after !== undefined) {
      const next = document.createElement('span');
      next.textContent = `→ ${formatStat(stat, after[stat])}`;
      if (after[stat] > now[stat]) next.className = 'smith__gain';
      table.append(next);
    }
  }
  table.dataset.columns = after === undefined ? '2' : '3';
  return table;
}
